/**
 * SIL-1708 (security B1). Every serial, drive key, version and cross-check subject on the attach screen is an AGENT CLAIM:
 * pair/start is unauthenticated, so a tampered agent can send a string that LOOKS like the run's own (homoglyphs, zero-width
 * or bidi characters, trailing spaces). The approver must never compare two such strings by eye, so they are rendered through
 * this function: printable ASCII stays as typed, everything else (controls, non-ASCII, zero-width, bidi, lone surrogates, a space at either end or doubled)
 * becomes a visible `\u{XXXX}` token, and a literal backslash is doubled so that an attacker cannot type a fake token.
 * Length is capped on the INPUT so a huge claim cannot flood the page.
 */
export interface AgentTextPart {
  text: string;
  /** True for a `\u{XXXX}` token (or the truncation marker): rendered highlighted. */
  escaped: boolean;
}

export interface AgentTextResult {
  parts: AgentTextPart[];
  /** Flat text of all parts (what a screen reader and a test see). */
  text: string;
  /** At least one character was made visible. */
  hadHidden: boolean;
  truncated: boolean;
}

export const AGENT_TEXT_DEFAULT_MAX = 64;
/** Hard bound on how much of a claim is looked at at all (UTF-16 units): the rest is counted as "more" and never walked. */
export const AGENT_TEXT_INPUT_CAP = 100_000;

export function escapeAgentString(raw: string, maxCodePoints: number = AGENT_TEXT_DEFAULT_MAX): AgentTextResult {
  const parts: AgentTextPart[] = [];
  let plain = '';
  let hadHidden = false;
  let truncated = false;
  const flush = () => {
    if (plain !== '') parts.push({ text: plain, escaped: false });
    plain = '';
  };
  // Bound the input BEFORE touching it, then collect only max + 1 code points (the extra one is the lookahead for the
  // trailing-space check and proves truncation). The remainder is counted, never stored.
  const bounded = raw.length > AGENT_TEXT_INPUT_CAP ? raw.slice(0, AGENT_TEXT_INPUT_CAP) : raw;
  const inputCut = bounded.length < raw.length;
  const chars: string[] = [];
  let total = 0;
  for (const ch of bounded) {
    total += 1;
    if (chars.length <= maxCodePoints) chars.push(ch);
  }
  // A space at either end, or next to another space, cannot be seen: make it a token ("trailing spaces" is a spoofing trick).
  const lastIndex = total - 1;
  const hiddenSpace = (i: number) => chars[i] === ' ' && (i === 0 || i === lastIndex || chars[i - 1] === ' ' || chars[i + 1] === ' ');
  let n = 0;
  for (const [i, ch] of chars.entries()) {
    if (n >= maxCodePoints) {
      truncated = true;
      break;
    }
    n += 1;
    const cp = ch.codePointAt(0)!;
    if (ch === '\\') {
      plain += '\\\\';
    } else if (cp >= 0x20 && cp <= 0x7e && !hiddenSpace(i)) {
      plain += ch;
    } else {
      flush();
      hadHidden = true;
      parts.push({ text: `\\u{${cp.toString(16).toUpperCase().padStart(4, '0')}}`, escaped: true });
    }
  }
  flush();
  if (inputCut) truncated = true;
  if (truncated) parts.push({ text: `...(+${total - Math.min(total, maxCodePoints)}${inputCut ? '+' : ''} more)`, escaped: true });
  return { parts, text: parts.map((p) => p.text).join(''), hadHidden, truncated };
}
